import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getWildfireGeoEventIcon } from 'features/mapping/icons/icons';
import { getFloodingEventIcon } from 'features/mapping/icons/iconData/floodingIcons';
import { getGeoEventDetailPath } from 'features/reports/reports.utils';
import { RelatedHazardGeoEvent } from 'features/hazards/hazard.types';

type RelatedGeoEventsProps = {
  geoEvents: RelatedHazardGeoEvent[];
};

const getGeoEventIcon = (geoEvent: RelatedHazardGeoEvent): { data: string } => {
  if (geoEvent.geoEventType === 'flooding') {
    const { isActive, data, notificationType } = geoEvent;
    return getFloodingEventIcon({ isActive, data, notificationType });
  }
  const { isActive, data, notificationType } = geoEvent;
  return getWildfireGeoEventIcon({ isActive, data, notificationType });
};

const getRegionsText = (
  regions?: { id: number; displayName: string }[],
): string => {
  if (!regions?.length) return '';
  return regions.map((r) => r.displayName).join(', ');
};

export const RelatedGeoEvents = (
  props: RelatedGeoEventsProps,
): JSX.Element | null => {
  const { geoEvents } = props;
  const { t } = useTranslation();
  const history = useHistory();

  if (!geoEvents.length) return null;

  const handleGeoEventClick = (geoEvent: RelatedHazardGeoEvent): void => {
    const path = getGeoEventDetailPath(geoEvent.geoEventType, geoEvent.id);

    history.push(path, {
      fromRoute: window.location.pathname + window.location.search,
    });
  };

  return (
    <List
      subheader={
        <ListSubheader variant="watchduty-section-header">
          <Typography variant="h3">
            {t('hazard.sections.relatedIncidents')}
          </Typography>
        </ListSubheader>
      }
    >
      {geoEvents.map((geoEvent) => (
        <ListItemButton
          key={geoEvent.id}
          divider
          onClick={() => handleGeoEventClick(geoEvent)}
        >
          <ListItemIcon sx={{ minWidth: 48 }}>
            <img
              src={getGeoEventIcon(geoEvent).data}
              alt=""
              width={40}
              style={{ opacity: geoEvent.isActive ? 1 : 0.5 }}
            />
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography sx={{ fontWeight: 'fontWeightBold' }}>
                {geoEvent.name}
              </Typography>
            }
            secondary={getRegionsText(geoEvent.regions)}
            slotProps={{
              secondary: {
                variant: 'body1',
                color: 'textSecondary',
                sx: {
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              },
            }}
          />
          <ChevronRightIcon />
        </ListItemButton>
      ))}
    </List>
  );
};
